import { useEffect, useState, type CSSProperties } from 'react';
import { DEFAULT_ACCENT, accentTones } from '@provalia/shared';
import { Card, ErrorBox, useToast } from '../../../shared/ui';
import { api, tenantPath } from '../../api';
import { FormField } from '../../components/FormField';
import type { TenantTabProps } from '../TenantDetail';

const HEX = /^#[0-9a-fA-F]{6}$/;

export function BrandingCard({ slug, detail, reload }: TenantTabProps): JSX.Element {
  const toast = useToast();
  const { branding } = detail.tenant;
  const [displayName, setDisplayName] = useState(branding.displayName);
  const [color, setColor] = useState(branding.accentColor);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setDisplayName(branding.displayName);
    setColor(branding.accentColor);
  }, [branding.displayName, branding.accentColor]);

  const validColor = HEX.test(color);
  const dirty = displayName !== branding.displayName || color.toLowerCase() !== branding.accentColor.toLowerCase();
  const previewColor = validColor ? color : branding.accentColor;
  const tones = accentTones(previewColor);
  const preview = {
    '--accent': previewColor,
    '--accent-ink': tones.ink,
    '--accent-text-light': tones.textLight,
    '--accent-text-dark': tones.textDark,
  } as CSSProperties;

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`${tenantPath(slug)}/branding`, { displayName: displayName.trim(), accentColor: color });
      toast('Marca guardada', 'ok');
      await reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Marca del portal">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <FormField label="Nombre visible" hint="Lo que ven proveedores y usuarios en la cabecera y en los correos.">
          <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </FormField>
        <div className="field">
          <label htmlFor={`accent-${slug}`}>Color de acento</label>
          <div className="color-input">
            <input
              type="color"
              aria-label="Elegir color en la paleta"
              value={validColor ? color : branding.accentColor}
              onChange={(e) => setColor(e.target.value)}
            />
            <input
              id={`accent-${slug}`}
              className="input mono"
              value={color}
              maxLength={7}
              spellCheck={false}
              aria-invalid={validColor ? undefined : true}
              aria-describedby={`accent-${slug}-desc`}
              onChange={(e) => setColor(e.target.value.trim())}
            />
          </div>
          <div className={validColor ? 'hint' : 'error'} id={`accent-${slug}-desc`}>
            {validColor ? 'Botones, enlaces y la franja superior del portal.' : `Formato #rrggbb, p. ej. ${DEFAULT_ACCENT} (el oro Provalia)`}
          </div>
        </div>

        <div className="brand-preview" style={preview} aria-label="Vista previa">
          <div className="brand-preview-bar" />
          <div className="brand-preview-body">
            <strong>{displayName.trim() || 'Nombre del cliente'}</strong>
            <span className="brand-preview-link">Pedidos</span>
            <span className="btn primary sm" aria-hidden="true">
              Aceptar pedido
            </span>
          </div>
        </div>

        <ErrorBox error={error} />
        <div className="row end">
          <button type="submit" className="btn primary" disabled={busy || !dirty || !validColor || !displayName.trim()}>
            {busy ? 'Guardando…' : 'Guardar marca'}
          </button>
        </div>
      </form>
    </Card>
  );
}
